import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { activations } from "../../lib/api";

export default function DashboardView() {
  const { user } = useAuth();
  const { t } = useLang();
  const [counts, setCounts] = useState({ squares: 0, districts: 0, unique: 0 });

  const CATEGORIES = [
    {
      id: "A",
      title: t("app.cat_a_title"),
      desc: t("app.cat_a_desc"),
      squaresTarget: 150,
      districtsTarget: 18,
    },
    {
      id: "B",
      title: t("app.cat_b_title"),
      desc: t("app.cat_b_desc"),
      squaresTarget: 100,
      districtsTarget: 13,
    },
    {
      id: "C",
      title: t("app.cat_c_title"),
      desc: t("app.cat_c_desc"),
      squaresTarget: 50,
      districtsTarget: 13,
    },
  ];

  useEffect(() => {
    if (!user) return;
    activations
      .list()
      .then((data) => {
        const items = Array.isArray(data) ? data : data?.items || [];
        const uniqueSquares = new Set(
          items.map((i) => i.square || i.grid).filter(Boolean),
        );
        const uniqueDistricts = new Set(
          items.map((i) => i.district || i.district_code).filter(Boolean),
        );
        setCounts({
          squares: uniqueSquares.size,
          districts: uniqueDistricts.size,
          unique: uniqueSquares.size,
        });
      })
      .catch(() => {});
  }, [user]);

  return (
    <div className="px-4 pt-6 pb-6 space-y-5" data-testid="dashboard-view">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {t("app.tab_progress")}
        </h1>
        <p className="text-white/45 text-sm">
          {user
            ? `${user.username || user.email}`
            : t("app.progress_subtitle_local")}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="stat-cards">
        <div className="ios-card p-4">
          <p className="text-3xl font-display font-bold">{counts.squares}</p>
          <p className="text-[10px] text-white/50 uppercase tracking-wider font-semibold mt-1">
            {t("app.stat_squares")}
          </p>
        </div>
        <div className="ios-card p-4">
          <p className="text-3xl font-display font-bold">{counts.districts}</p>
          <p className="text-[10px] text-white/50 uppercase tracking-wider font-semibold mt-1">
            {t("app.stat_districts")}
          </p>
        </div>
        <div className="ios-card p-4">
          <p className="text-3xl font-display font-bold">{counts.unique}</p>
          <p className="text-[10px] text-white/50 uppercase tracking-wider font-semibold mt-1">
            {t("app.stat_unique")}
          </p>
        </div>
      </div>

      <p className="text-[10px] text-white/50 uppercase tracking-wider font-semibold pt-2">
        {t("app.diploma_categories")}
      </p>
      <div className="grid grid-cols-2 gap-3" data-testid="role-cards">
        <div className="ios-card p-4">
          <Badge className="bg-[#007AFF]/15 text-[#007AFF] border-[#007AFF]/30 mb-2 font-bold">
            📡 ACTIVATOR
          </Badge>
          <p className="text-3xl font-display font-bold mt-2">
            {counts.districts}
          </p>
          <p className="text-xs text-white/45 mt-1">
            {counts.districts} {t("app.stat_districts").toLowerCase()}
          </p>
        </div>
        <div className="ios-card p-4">
          <Badge className="bg-[#FF453A]/15 text-[#FF453A] border-[#FF453A]/30 mb-2 font-bold">
            🎯 HUNTER
          </Badge>
          <p className="text-3xl font-display font-bold mt-2">0</p>
          <p className="text-xs text-white/45 mt-1">
            0 {t("app.stat_districts").toLowerCase()}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {CATEGORIES.map((c) => {
          const sqPct = Math.min(100, (counts.squares / c.squaresTarget) * 100);
          const dPct = Math.min(
            100,
            (counts.districts / c.districtsTarget) * 100,
          );
          return (
            <div
              key={c.id}
              className="ios-card p-4 space-y-3"
              data-testid={`category-${c.id}`}
            >
              <div>
                <Badge className="bg-[#007AFF]/15 text-[#007AFF] border-[#007AFF]/25 text-[10px] mb-2">
                  {t("app.in_progress")}
                </Badge>
                <h3 className="font-semibold">{c.title}</h3>
                <p className="text-xs text-white/45 mt-1">{c.desc}</p>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/50 uppercase tracking-wider font-semibold">
                      Squares
                    </span>
                    <span className="font-mono">
                      {counts.squares} / {c.squaresTarget}
                    </span>
                  </div>
                  <Progress
                    value={sqPct}
                    className="h-1.5 bg-white/5 [&>div]:bg-[#007AFF]"
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/50 uppercase tracking-wider font-semibold">
                      Districts
                    </span>
                    <span className="font-mono">
                      {counts.districts} / {c.districtsTarget}
                    </span>
                  </div>
                  <Progress
                    value={dPct}
                    className="h-1.5 bg-white/5 [&>div]:bg-[#FF6B35]"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
